import React from "react";

import clsx from "clsx";

import { type ApolloError } from "@apollo/client";
import { GenericTooltip } from "@components/tooltip-container";
import { jsonHash } from "@north-beam/nb-common";

import { HeaderMenu } from "../table/header-menu";
import { SortDirection } from "../table/sort-direction";
import { type Sorting } from "../types";

interface Row {
  visitsLast30d: number;
  name: string;
  identificationMethod: string;
  matchResult: any;
  metadata: any;
}

interface Props {
  rows: Row[];
  simulatedSegment: { name: string; priority: number };
  isDataPresent: boolean;
  loading: boolean;
  error?: ApolloError;
  sorting: Sorting;
  onChangeSorting: (sorting: Sorting) => void;
}

export const LabelRuleSimulatorTable = ({
  rows,
  simulatedSegment,
  isDataPresent,
  loading,
  error,
  sorting,
  onChangeSorting,
}: Props) => {
  return (
    <table className="text-xs table-fixed	border-spacing-0 border-separate min-w-[100%]">
      <thead>
        <tr>
          <th className="h-[36px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Primary matched rule</div>
              <div className="flex items-center gap-1">
                <HeaderMenu
                  name="priorityOrderedMatchedRules"
                  onChange={onChangeSorting}
                />
                <SortDirection
                  name="priorityOrderedMatchedRules"
                  sorting={sorting}
                  onClick={onChangeSorting}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[36px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Name</div>
              <div className="flex items-center gap-1">
                <HeaderMenu name="name" onChange={onChangeSorting} />
                <SortDirection
                  name="name"
                  sorting={sorting}
                  onClick={onChangeSorting}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[36px] w-[140px] min-w-[140px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">ID method</div>
              <div className="flex items-center gap-1">
                <HeaderMenu
                  name="identificationMethod"
                  onChange={onChangeSorting}
                />
                <SortDirection
                  name="identificationMethod"
                  sorting={sorting}
                  onClick={onChangeSorting}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[36px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Output label</div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[36px] w-[140px] min-w-[140px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Visits 30D</div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[36px] w-[80px] min-w-[80px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Metadata</div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {error && (
          <tr className="group odd:bg-white even:bg-[#fafafe]">
            <td
              className="border-gray-2 align-top border-b group-last:border-b-0"
              colSpan={6}
            >
              <div className="p-2 text-sm h-[120px]">
                Click “simulate” to view matches
              </div>
            </td>
          </tr>
        )}
        {!isDataPresent && (
          <tr className="group odd:bg-white even:bg-[#fafafe]">
            <td
              className="border-gray-2 align-top border-b group-last:border-b-0"
              colSpan={6}
            >
              <div className="p-2 text-sm h-[120px]">
                {loading ? "Loading..." : "Click “simulate” to view matches"}
              </div>
            </td>
          </tr>
        )}
        {isDataPresent && (
          <>
            {rows.map((row) => {
              const matchResult = row.matchResult;
              const firstMatch = matchResult.priorityOrderedMatchedRules[0];
              const isCurrentRule =
                firstMatch &&
                firstMatch.name === simulatedSegment.name &&
                firstMatch.priority === simulatedSegment.priority;

              return (
                <tr
                  key={jsonHash(row)}
                  className={clsx("group", {
                    "odd:bg-[#fafafe] even:bg-white": !isCurrentRule,
                    "bg-nebula-1": isCurrentRule,
                  })}
                >
                  <td className="border-r border-gray-2 border-b group-last:border-b-0">
                    <div className="p-2 text-sm">
                      {firstMatch ? (
                        <GenericTooltip
                          content={
                            <>
                              <p className="mb-2">All matched rules:</p>
                              <table className="table table-sm table-bordered text-xs">
                                <thead>
                                  <tr className="text-xxs">
                                    <th className="text-right px-3">Rank</th>
                                    <th className="px-3">Name</th>
                                    <th className="px-3">Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {matchResult.priorityOrderedMatchedRules.map(
                                    (v: any, idx: number) => (
                                      <tr
                                        key={jsonHash(v)}
                                        className={clsx({
                                          "bg-nebula-1": idx === 0,
                                        })}
                                      >
                                        <td className="text-right px-3">
                                          {v.priority}
                                        </td>
                                        <td className="px-3">{v.name}</td>
                                        <td className="px-3">{v.value}</td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                              </table>
                            </>
                          }
                        >
                          <span className="font-bold">
                            {firstMatch.name} (rank: {firstMatch.priority})
                          </span>
                        </GenericTooltip>
                      ) : (
                        <span className="text-muted">(no match)</span>
                      )}
                    </div>
                  </td>
                  <td className="border-r border-gray-2 border-b group-last:border-b-0">
                    <div className="p-2 text-sm">
                      {row.name || (
                        <span className="text-muted">(no name)</span>
                      )}
                    </div>
                  </td>
                  <td className="border-r border-gray-2 border-b group-last:border-b-0">
                    <div className="p-2 text-sm">
                      {row.identificationMethod}
                    </div>
                  </td>
                  <td className="border-r border-gray-2 border-b group-last:border-b-0">
                    <div className="p-2 text-sm">
                      {row.matchResult.outputValue ? (
                        <span className="font-bold">
                          {row.matchResult.outputValue}
                        </span>
                      ) : (
                        <span className="text-muted">(none)</span>
                      )}
                    </div>
                  </td>
                  <td className="border-r border-gray-2 border-b group-last:border-b-0">
                    <div className="p-2 text-sm text-right">
                      {row.visitsLast30d ?? "N/A"}
                    </div>
                  </td>
                  <td className="border-gray-2 border-b group-last:border-b-0">
                    <div className="p-2 text-sm">
                      <GenericTooltip
                        content={
                          <pre className="text-venus">
                            {JSON.stringify(row.metadata, null, 2)}
                          </pre>
                        }
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </>
        )}
      </tbody>
    </table>
  );
};
